import React, { useState } from "react";
import { observer } from "mobx-react";
import { isEqual } from "lodash";
import Searchbar from "@search/Searchbar";
import SearchForm from "@search/SearchForm";
import * as filters from "@search/filters";
import { AccommodationFilterManager } from "./AccommodationFilterManager";
import { initialSearchParams as initial } from "@search/GeneralSearchStore";
import { makeStyles, Typography, Grid } from "@material-ui/core";
import {
  AccommodationOfferOptions,
  AccommodationSwitches,
  AccommodationType,
  useGetUserSelectUsersQuery
} from "@graphql";
import { useLocale } from "@hooks";

const Grid3 = filters.Grid3;

type Props = {
  manager: AccommodationFilterManager;
};

const AccommodationSearchForm: React.FC<Props> = (props) => {
  const [users, setUsers] = useState<LabelValue[]>([]);
  const { locale } = useLocale();
  const classes = useStyles({});
  const { manager } = props;

  useGetUserSelectUsersQuery({
    onCompleted(data) {
      setUsers(
        data.users.map((x) => ({
          label: x.Name,
          value: x._id
        }))
      );
    }
  });

  const searchbar = (
    <>
      <Typography variant={"h6"}>{locale.mainMenu.accommodations}</Typography>
      <Searchbar
        onChange={(v) => manager.updateFilter({ text: v }, true)}
        value={manager.filter.text}
      />
    </>
  );

  const ba = filters.renderSelect(
    locale.ResponsibleHousingAgent,
    users,
    false,
    manager.filter.responsiblePersonId,
    (v) => manager.updateFilter({ responsiblePersonId: v }, true),

    false,
    manager.isFilterDirty("responsiblePersonId")
  );

  const location = (
    <Grid3
      dirty={!isEqual(manager.filter.location, initial.location)}
      className={classes.locationWrap}
    >
      <div>
        {filters.renderZipCityField(
          locale.ZipOrPlace,
          manager.filter.location ? manager.filter.location.zipOrCity : "",
          (v) =>
            manager.updateFilter(
              {
                location: { ...manager.filter.location, zipOrCity: v }
              },
              true
            ),
          false
        )}
      </div>
      <div className={classes.area}>
        {filters.renderZipCityField(
          "Klm",
          manager.filter.location ? manager.filter.location.klm : "20",
          (v) =>
            manager.updateFilter(
              {
                location: { ...manager.filter.location, klm: v.replace(/[^+\d]/g, "") }
              },
              true
            ),
          false
        )}
      </div>
    </Grid3>
  );

  const optionsAccType = Object.values(AccommodationType).map((v) => ({
    label: locale.accommodationType[v],
    value: v
  }));

  const type = filters.renderSelect(
    locale.AccommodationType,
    optionsAccType,
    false,
    manager.filter.type || null,
    (v: AccommodationType) => manager.updateFilter({ type: v }),
    false,
    manager.isFilterDirty("type")
  );

  const accOffersOpt = Object.values(AccommodationOfferOptions).map((v) => ({
    label: locale.accommodationOfferOptions[v],
    value: v
  }));

  const switchesOpt = Object.values(AccommodationSwitches).map((v) => ({
    label: locale.accommodationSwitchesOptions[v],
    value: v
  }));

  const offersOpt = filters.renderMultiSelect(
    locale.Options,
    accOffersOpt,
    false,
    manager.filter.options || [],
    (v: AccommodationOfferOptions[]) => manager.updateFilter({ options: v }),
    false,
    manager.isFilterDirty("options")
  );

  const switches = filters.renderMultiSelect(
    `${locale.EnvCertification}/${locale.PreferredChoice}`,
    switchesOpt,
    false,
    manager.filter.switches || [],
    (v: AccommodationSwitches[]) => manager.updateFilter({ switches: v }),
    false,
    manager.isFilterDirty("switches")
  );

  const rooms = filters.renderTextField(
    locale.Rooms,
    manager.filter.rooms,
    (v) => manager.updateFilter({ rooms: v }, true),
    manager.isFilterDirty("rooms"),
    false,
    { type: "number" }
  );

  const beds = filters.renderTextField(
    locale.Beds,
    manager.filter.beds,
    (v) => manager.updateFilter({ beds: v }, true),
    manager.isFilterDirty("beds"),
    false,
    { type: "number" }
  );

  return (
    <SearchForm searchbar={searchbar}>
      <Grid container spacing={2}>
        {location}
        {type}
        {offersOpt}
        {ba}
        {rooms}
        {beds}
        {switches}
      </Grid>
    </SearchForm>
  );
};

export default observer(AccommodationSearchForm);

const useStyles = makeStyles(() => ({
  locationWrap: {
    display: "flex"
  },
  area: {
    maxWidth: 80
  }
}));
